"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"

type Props = {
  data: any
}

// --- Icons ---

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <rect x="3" y="1" width="4" height="14" rx="1" />
      <rect x="9" y="1" width="4" height="14" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <path d="M3 1.5v13l11-6.5L3 1.5z" />
    </svg>
  )
}

function ArrowIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path
        d={dir === "left" ? "M20 12H4M10 6l-6 6 6 6" : "M4 12h16M14 6l6 6-6 6"}
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true)
      return
    }
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0, rootMargin: "0px 0px -15% 0px" },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;
`

/* mobile: text then media stacked. desktop: two columns — the media is flush to its
   outer edge (full-bleed on that side) and the text carries the 120px page padding. */
const Inner = styled.div<{ $mediaFirst: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding-top: 64px;
  /* when the media sits on top on mobile ($mediaFirst), the text + buttons are the
     last thing in the block — add bottom breathing room so the buttons aren't flush
     against the next component (ASH-1985). When the media is last it stays flush. */
  ${({ $mediaFirst }) => ($mediaFirst ? "padding-bottom: 48px;" : "")}

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`

/* first in JSX = reading-start (right) side in RTL */
const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 20px;
  /* title, body and secondary text are centered in the column, per Figma (ASH-1987) */
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 50%;
    max-width: 50%;
    min-width: 0;
    align-items: center;
    justify-content: center;
    padding: 64px clamp(24px, 8.33vw, 120px);

    > * {
      max-width: 576px;
    }
  }
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/* staggered entrance: title, then text, then media */
const Reveal = styled.div<{ $in: boolean; $delay: number }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "24px")});
  transition: opacity 0.6s ease, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => $delay}ms;
`

/* Rich-text title: the emphasized word carries the Figma brush underline. */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    position: relative;
    display: inline-block;
    font-weight: inherit;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;

    strong {
      font-weight: 700;
    }
  }
`

const BodyText = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: 1.45;
  }
`

/* short line shown directly above the CTA buttons */
const SecondaryText = styled.p`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 28px;
  }
`

/* Natural DOM order: button 1 sits at the reading start (right in RTL), button 2
   after it — so the on-screen order matches the Umbraco First/Second order
   (ASH-1983). The two buttons share the row as equal halves. */
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

/* Buttons are equal-width halves of the row (276px each on desktop, 160px on
   mobile per Figma) so a single button fills the row full-width (ASH-1989 /
   ASH-1991). button 1 = Filled (reading-start), button 2 = Outline (ASH-1983,
   matching the Figma where the filled button is on the reading-start side). */
const CtaButton = styled.a<{ $filled?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* min-height (not fixed height) so a long label wraps and grows the button
     instead of overflowing its 276/160px cell (PR #533 review) */
  min-height: 36px;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid var(--comp-heading);
  background-color: ${({ $filled }) => ($filled ? "var(--comp-heading)" : "transparent")};
  color: ${({ $filled }) => ($filled ? "var(--comp-bg)" : "var(--comp-heading)")};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-heading);
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: 48px;
    font-size: 20px;
  }
`

/* the reveal wrapper is the flex child, so it carries the column sizing —
   otherwise it collapses to zero width around the absolutely-positioned slides */
const MediaSlot = styled(Reveal)`
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 0;
    min-width: 0;
    align-self: stretch;
  }
`

const MediaColumn = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  /* square on mobile (375x375), full-height 680x734 column on desktop, per Figma
     — same size for both the V1 and flipped variants (ASH-1988) */
  aspect-ratio: 1 / 1;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    aspect-ratio: auto;
    min-height: 734px;
  }
`

/* gallery slides cross-fade */
const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.6s ease;
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
`

const MediaImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Controls = styled.div`
  position: absolute;
  bottom: 16px;
  inset-inline-start: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 2;
`

const RoundButton = styled.button<{ $size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size || 32}px;
  height: ${({ $size }) => $size || 32}px;
  border-radius: 50%;
  border: 1px solid #000;
  background: #fff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const PlayPauseBtn = styled(RoundButton)`
  border: none;
`

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 100px;
  cursor: pointer;
  border: ${({ $active }) => ($active ? "none" : "1px solid #fff")};
  background-color: ${({ $active }) => ($active ? "#fff" : "transparent")};
`

// --- Parsing ---

type MediaItem = { url: string; alt: string }
type LinkItem = { name: string; url: string; target?: string } | null

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

function parseLink(raw: any): LinkItem {
  const link = Array.isArray(raw) ? raw[0] : undefined
  if (!link?.name) return null
  return {
    name: link.name,
    url: getLinkHrefFromMultiUrlPickerItem(link) || "#",
    target: link.target,
  }
}

// --- Main Component ---

function AboutOpening3({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref, inView } = useInViewOnce<HTMLDivElement>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [slide, setSlide] = useState(0)

  const title = (extractProperty(data, "title") as string) || ""
  const bodyText = (extractProperty(data, "bodyText") as string) || ""
  const secondaryText = (extractProperty(data, "secondaryText") as string) || ""
  const rawMedia = (extractProperty(data, "media", true) as any[] | undefined) || []
  const mediaItems: MediaItem[] = rawMedia
    .filter((m) => m?.url)
    .map((m) => ({ url: m.url as string, alt: (m.altText as string) || "" }))
  const positionRaw = String(extractProperty(data, "contentPosition") || "").toLowerCase()
  const button1 = parseLink(extractProperty(data, "firstButton"))
  const button2 = parseLink(extractProperty(data, "secondButton"))

  /* Default matches the Figma "V1_Right" variant: media on the reading-start (right)
     side. "contentRight" / "mediaLeft" flips it. */
  const mediaFirst = !(positionRaw.includes("contentright") || positionRaw.includes("medialeft"))

  const isVideo = mediaItems.length === 1 && isVideoUrl(mediaItems[0].url)
  const isGallery = mediaItems.length > 1

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.aboutOpening)
    setIsPlaying(!isPlaying)
  }

  const go = (delta: number) => setSlide((s) => (s + delta + mediaItems.length) % mediaItems.length)

  if (!title && !bodyText && !secondaryText && mediaItems.length === 0) return null

  const linkProps = (l: NonNullable<LinkItem>) =>
    l.target === "_blank" ? { target: "_blank", rel: "noopener noreferrer" } : {}

  const media = mediaItems.length > 0 && (
    <MediaSlot $in={inView} $delay={300}>
      <MediaColumn>
        {isVideo ? (
          <>
            <MediaVideo
              ref={videoRef}
              src={getUmbracoMediaUrl(mediaItems[0].url)}
              autoPlay
              muted
              loop
              playsInline
            />
            <Controls>
              <PlayPauseBtn
                $size={40}
                onClick={togglePlay}
                aria-label={
                  isPlaying
                    ? isRtl
                      ? "עצור תנועת הרכיב"
                      : "Stop animation"
                    : isRtl
                    ? "הפעל תנועה רכיב"
                    : "Play animation"
                }
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </PlayPauseBtn>
            </Controls>
          </>
        ) : (
          <>
            {mediaItems.map((item, i) => (
              <Slide
                key={i}
                $active={i === slide}
                aria-hidden={i !== slide || undefined}
                {...(i !== slide ? { inert: "" } : {})}
              >
                {/* a video mixed into a multi-item gallery must render as a
                    <video>, not through next/image which showed it broken (ASH-1986) */}
                {isVideoUrl(item.url) ? (
                  <MediaVideo src={getUmbracoMediaUrl(item.url)} autoPlay muted loop playsInline />
                ) : (
                  <MediaImage
                    src={getUmbracoMediaUrl(item.url)}
                    fill
                    alt={item.alt}
                    sizes="(max-width: 768px) 100vw, 680px"
                  />
                )}
              </Slide>
            ))}
            {isGallery && (
              <Controls>
                <RoundButton onClick={() => go(-1)} aria-label={isRtl ? "הקודם" : "Previous"}>
                  <ArrowIcon dir={isRtl ? "right" : "left"} />
                </RoundButton>
                <Dots>
                  {mediaItems.map((_, i) => (
                    <Dot
                      key={i}
                      $active={i === slide}
                      onClick={() => setSlide(i)}
                      aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                    />
                  ))}
                </Dots>
                <RoundButton onClick={() => go(1)} aria-label={isRtl ? "הבא" : "Next"}>
                  <ArrowIcon dir={isRtl ? "left" : "right"} />
                </RoundButton>
              </Controls>
            )}
          </>
        )}
      </MediaColumn>
    </MediaSlot>
  )

  const text = (
    <TextColumn>
      <Reveal $in={inView} $delay={0}>
        <TitleGroup>{title && <Title dangerouslySetInnerHTML={{ __html: title }} />}</TitleGroup>
      </Reveal>
      {(bodyText || secondaryText || button1 || button2) && (
        <Reveal $in={inView} $delay={150}>
          <TitleGroup>
            {bodyText && <BodyText>{bodyText}</BodyText>}
            {secondaryText && <SecondaryText>{secondaryText}</SecondaryText>}
            {(button1 || button2) && (
              <Actions>
                {/* button 1 = filled + reading-start (right), button 2 = outline,
                    matching Figma and the Umbraco First/Second order (ASH-1983).
                    onClick smooth-scrolls when the link targets an on-page anchor
                    such as the Lead Form (ASH-1984). */}
                {button1 && (
                  <CtaButton
                    $filled
                    href={button1.url}
                    onClick={(e) => {
                      analytics.ctaClick(button1.name, LANDING_ELEMENTS.aboutOpening)
                      handleAnchorScroll(e, button1.url)
                    }}
                    {...linkProps(button1)}
                  >
                    {button1.name}
                  </CtaButton>
                )}
                {button2 && (
                  <CtaButton
                    href={button2.url}
                    onClick={(e) => {
                      analytics.ctaClick(button2.name, LANDING_ELEMENTS.aboutOpening)
                      handleAnchorScroll(e, button2.url)
                    }}
                    {...linkProps(button2)}
                  >
                    {button2.name}
                  </CtaButton>
                )}
              </Actions>
            )}
          </TitleGroup>
        </Reveal>
      )}
    </TextColumn>
  )

  return (
    <Section ref={ref}>
      <Inner $mediaFirst={mediaFirst}>
        {mediaFirst ? (
          <>
            {media}
            {text}
          </>
        ) : (
          <>
            {text}
            {media}
          </>
        )}
      </Inner>
    </Section>
  )
}

export default AboutOpening3
